import { useState, useEffect, useMemo } from 'react';
import { customerContactsApi, customersApi } from '../../../services/api';
import { CustomerContactListItem } from '../../../types/customer-contact';
import { Customer, PaginatedResponse } from '../../../types';

export const useContactsList = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState<number | ''>('');
  const [data, setData] = useState<PaginatedResponse<CustomerContactListItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const load = async (page?: number) => {
    try {
      setLoading(true);
      const res = await customerContactsApi.getAll({
        customer_id: selectedCustomerFilter === '' ? undefined : Number(selectedCustomerFilter),
        page,
      });
      setData(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await customersApi.getAll({ per_page: 1000 });
      setCustomers(response.data || []);
    } catch (e: any) {
      console.error('Failed to load customers:', e);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerFilter]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredContacts = useMemo(() => {
    if (!data?.data) return [];
    if (!debouncedSearchQuery.trim()) return data.data;

    const query = debouncedSearchQuery.toLowerCase();
    return data.data.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        (c.email && c.email.toLowerCase().includes(query)) ||
        (c.phone && c.phone.toLowerCase().includes(query)) ||
        (c.title && c.title.toLowerCase().includes(query)) ||
        (c.customer?.item && c.customer.item.toLowerCase().includes(query)),
    );
  }, [data?.data, debouncedSearchQuery]);

  return {
    customers,
    selectedCustomerFilter,
    setSelectedCustomerFilter,
    data,
    loading,
    error,
    setError,
    searchQuery,
    setSearchQuery,
    filteredContacts,
    load,
  };
};
